/**
 * What Felix does when someone taps a quick-reply button.
 *
 * A WhatsApp quick-reply button carries no payload of its own — the *label* is
 * what comes back to the bot. So the label is not cosmetic: it is the command,
 * and a label the bot does not recognise silently dead-ends the recipient on
 * the "didn't quite catch that" fallback. This module lets the CRM tell an
 * operator, while they type, whether Felix will understand the label.
 *
 * ## The leading slash does not matter
 *
 * Both of the bot's matchers drop it before comparing — the create path folds
 * the label with `normalize_intent_text` (punctuation becomes a space), and the
 * consent path does a literal `lstrip("/")`. So `/create` and `Create` are the
 * same command. What actually matters is whether the *phrase* is one of the
 * known ones.
 *
 * ## Two delivery paths
 *
 * WhatsApp delivers a quick-reply tap either as a plain text message or as an
 * interactive callback, and the bot matches them in different places with
 * different (overlapping, not identical) phrase sets. A label matched by only
 * one path works only when WhatsApp happens to use that path, so this module
 * reports which paths accept a label rather than a single yes/no.
 *
 * ## Every served language
 *
 * The bot also accepts its own create-button titles in every catalog language
 * ("Crea il mio Echo", "Создать моё Echo"), each catalog's create phrases and
 * `/create` alias ("создать"), and the catalogs' STOP words (only Russian
 * ships any: "стоп", "отписаться", …) — whole messages only, so "стоп-кадр из
 * фильма" is never a STOP. Those words live in `botIntentVocabulary.ts`.
 *
 * ## Mirrored from the bot — keep in sync
 *
 * These sets are duplicated from `echoflicks-whatsapp-bot`; the bot is a
 * separate service and does not publish them. `botButtonIntents.test.ts` pins
 * every value so a drift shows up as a failing test rather than a dead button.
 * Sources (origin/main e25aa4c, 2026-10-11):
 *   - `bot/i18n/normalize.py`            — `normalize_intent_text`
 *     (mirrored in `intentNormalisation.ts`)
 *   - `bot/handlers/message_handler.py`  — `_CREATE_INTENT_TEXTS`,
 *     `is_create_intent_text`, `_localized_create_phrases`, `process_command`
 *   - `bot/handlers/callback_handler.py` — `_is_create_button_callback`,
 *     `_localized_create_button_phrases`, `_is_stop_button_callback`
 *   - `bot/constants/consent.py`         — `STOP_KEYWORDS`,
 *     `match_consent_keyword`
 *   - `bot/i18n/vocabulary.py`           — `localized_phrases`
 */

import { createButtonPhrases, localizedPhrases } from "./botIntentVocabulary";
import { normalizeIntentText } from "./intentNormalisation";

export type ButtonIntent = "create" | "stop";

/** Which of the bot's two delivery paths accept a label. */
export type ButtonIntentDelivery = "both" | "text_only" | "callback_only";

export type ButtonIntentMatch = {
  intent: ButtonIntent;
  delivery: ButtonIntentDelivery;
};

/**
 * The bot's quick-reply normalisation, `normalize_intent_text`: NFKC, full
 * case folding, "ё" -> "е", every run of characters that are not letters or
 * digits (of any script) becomes one space. Accents and non-Latin letters are
 * kept, never stripped — "Créer mon Echo" -> "créer mon echo", "Стоп!" ->
 * "стоп" — exactly as the bot compares them, so an accented spelling of an
 * English phrase does NOT match ("Créate my movie" is not "create my movie").
 */
export function normalizeButtonIntent(
  value: string | null | undefined,
): string {
  return normalizeIntentText(value);
}

/** The bot's consent normalization: trim, lowercase, drop a leading slash. */
function normalizeConsentKeyword(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/^\/+/, "")
    .trim();
}

// message_handler._CREATE_INTENT_TEXTS — matched against the normalized text.
const CREATE_TEXT_PHRASES = [
  "create",
  "create my movie",
  "create movie",
  "start my movie",
  "create my echo",
  "create echo",
  "make my movie",
  "create a new echo",
  "i bought my echo let s go",
  // Spanish free-text asks (also in the es catalog's CREATE_INTENT_PHRASES).
  "crear echo",
  "crear un echo",
  "crear nuevo echo",
  "crear un nuevo echo",
] as const;

// message_handler._localized_create_phrases — the create-button titles of
// every language plus each catalog's create phrases and /create alias.
const LOCALIZED_CREATE_TEXT_PHRASES: ReadonlySet<string> = new Set([
  ...createButtonPhrases(),
  ...localizedPhrases("CREATE_INTENT_PHRASES"),
  ...localizedPhrases("COMMAND_ALIASES_CREATE"),
]);

// callback_handler._is_create_button_callback — a narrower set than the text
// path, plus the exact ids "/create" and "create", plus the create-button
// titles of every language (English included).
const CREATE_CALLBACK_PHRASES = [
  "create my movie",
  "create movie",
  "start my movie",
] as const;
const CREATE_CALLBACK_EXACT_IDS = ["/create", "create"] as const;
const CREATE_BUTTON_PHRASES = createButtonPhrases();

// consent.STOP_KEYWORDS — matched against the whole consent-normalized text.
const STOP_TEXT_KEYWORDS = ["stop", "unsubscribe", "stop promotions"] as const;

// consent.match_consent_keyword — the catalogs' STOP words, whole message
// after normalize_intent_text (text path only).
const LOCALIZED_STOP_KEYWORDS = localizedPhrases("STOP_KEYWORDS");

// callback_handler._is_stop_button_callback — note it accepts "stop promos",
// which the consent path above does NOT, and no localized STOP word.
const STOP_CALLBACK_PHRASES = [
  "stop",
  "stop promos",
  "stop promotions",
] as const;

function matchesCreateOnText(raw: string, normalized: string): boolean {
  // message_handler.process_command: the first whitespace-separated token is
  // exactly "/create" (so "/create debugmarker" still starts an Echo).
  if (raw.trim().split(/\s+/)[0]?.toLowerCase() === "/create") return true;
  return (
    (CREATE_TEXT_PHRASES as readonly string[]).includes(normalized) ||
    LOCALIZED_CREATE_TEXT_PHRASES.has(normalized)
  );
}

function matchesCreateOnCallback(raw: string, normalized: string): boolean {
  if (
    (CREATE_CALLBACK_EXACT_IDS as readonly string[]).includes(
      raw.trim().toLowerCase(),
    )
  ) {
    return true;
  }
  return (
    (CREATE_CALLBACK_PHRASES as readonly string[]).includes(normalized) ||
    CREATE_BUTTON_PHRASES.has(normalized)
  );
}

function deliveryOf(
  onText: boolean,
  onCallback: boolean,
): ButtonIntentDelivery | null {
  if (onText && onCallback) return "both";
  if (onText) return "text_only";
  if (onCallback) return "callback_only";
  return null;
}

/**
 * What Felix will do with this button label, or `null` if nothing — meaning the
 * recipient tapping it lands on the "didn't quite catch that" fallback.
 */
export function classifyButtonIntent(
  label: string | null | undefined,
): ButtonIntentMatch | null {
  const raw = String(label ?? "");
  if (!raw.trim()) return null;
  const normalized = normalizeButtonIntent(raw);
  const consent = normalizeConsentKeyword(raw);

  const createDelivery = deliveryOf(
    matchesCreateOnText(raw, normalized),
    matchesCreateOnCallback(raw, normalized),
  );
  if (createDelivery) return { intent: "create", delivery: createDelivery };

  const stopDelivery = deliveryOf(
    (STOP_TEXT_KEYWORDS as readonly string[]).includes(consent) ||
      LOCALIZED_STOP_KEYWORDS.has(normalized),
    (STOP_CALLBACK_PHRASES as readonly string[]).includes(normalized),
  );
  if (stopDelivery) return { intent: "stop", delivery: stopDelivery };

  return null;
}

export type ButtonLabelPreset = {
  label: string;
  intent: ButtonIntent;
  /** i18n key for the one-line description shown next to the preset. */
  descriptionKey: string;
};

/**
 * One-click labels for quick-reply buttons.
 *
 * Every preset here is accepted on BOTH delivery paths, so picking one removes
 * the question entirely. `/create` is kept because operators reach for it, and
 * it is genuinely equivalent to `Create` — the slash is stripped either way.
 */
export const BUTTON_LABEL_PRESETS: ButtonLabelPreset[] = [
  {
    label: "Create my movie",
    intent: "create",
    descriptionKey: "crm.whatsapp.buttons.preset_create",
  },
  {
    label: "Create movie",
    intent: "create",
    descriptionKey: "crm.whatsapp.buttons.preset_create",
  },
  {
    label: "Start my movie",
    intent: "create",
    descriptionKey: "crm.whatsapp.buttons.preset_create",
  },
  {
    label: "/create",
    intent: "create",
    descriptionKey: "crm.whatsapp.buttons.preset_create_command",
  },
  {
    label: "Stop promotions",
    intent: "stop",
    descriptionKey: "crm.whatsapp.buttons.preset_stop",
  },
];

export type ButtonIntentStatus = "recognized" | "partial" | "unrecognized";

export type ButtonIntentAdvice = {
  status: ButtonIntentStatus;
  intent: ButtonIntent | null;
  /** i18n key for the sentence shown under the button-text field. */
  messageKey: string;
};

/**
 * Advice for one quick-reply label.
 *
 * URL buttons never reach the bot — they open a link — so callers should skip
 * them rather than passing their label here.
 */
export function adviseButtonLabel(
  label: string | null | undefined,
): ButtonIntentAdvice {
  const match = classifyButtonIntent(label);
  if (!match) {
    return {
      status: "unrecognized",
      intent: null,
      messageKey: "crm.whatsapp.buttons.intent_unrecognized",
    };
  }
  if (match.delivery === "both") {
    return {
      status: "recognized",
      intent: match.intent,
      messageKey:
        match.intent === "create"
          ? "crm.whatsapp.buttons.intent_create"
          : "crm.whatsapp.buttons.intent_stop",
    };
  }
  return {
    status: "partial",
    intent: match.intent,
    messageKey:
      match.delivery === "text_only"
        ? "crm.whatsapp.buttons.intent_text_only"
        : "crm.whatsapp.buttons.intent_callback_only",
  };
}
